package com.evoapps.evoshift.widgets

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.core.LinearEasing
import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.LocalContentColor
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.State
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.staticCompositionLocalOf
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.Shape
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.graphics.isSpecified
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.clearAndSetSemantics
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.isSpecified
import com.evoapps.evoshift.ui.theme.LocalReducedMotion
import com.evoapps.evoshift.ui.theme.fadeThrough
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.loading
import org.jetbrains.compose.resources.stringResource

/*
 * SH-247: placeholders while a screen's data is not known yet. A section whose data is on its
 * way shows grey shapes the size of its content (a card, its title and lines, a list's rows), so
 * the screen keeps its layout and nothing jumps when the data arrives; [LoadingSwap] then fades
 * the placeholder into the content. Data from the offline cache is known at once: no placeholder.
 *
 * The shapes take the content colour around them (navy on the cream page and white cards, white
 * on the navy cards) at a low alpha, so they suit every colour combination. They pulse gently
 * (1.4 s), and stand still under reduced motion.
 */

/** Test tag of every placeholder area (robots: [SKELETON_TAG] shown before the data, gone after). */
const val SKELETON_TAG = "skeleton"

/** How strongly the shapes show over the content colour (0: invisible). */
private const val SKELETON_ALPHA = 0.08f

/** The pulse: the shapes' opacity breathes between this and 1. */
private const val PULSE_LOW = 0.55f
private const val PULSE_MS = 1_400

/** The current pulse (1 outside a [SkeletonArea], so a lone shape stands still). */
private val LocalSkeletonPulse = staticCompositionLocalOf<State<Float>> { mutableStateOf(1f) }

/**
 * A placeholder area: one node for screen readers and tests ("Loading…", [SKELETON_TAG]) and one
 * pulse for all the shapes in it, so they breathe together.
 */
@Composable
fun SkeletonArea(modifier: Modifier = Modifier, content: @Composable ColumnScope.() -> Unit) {
    val description = stringResource(Res.string.loading)
    val pulse: State<Float> = if (LocalReducedMotion.current) {
        remember { mutableStateOf(1f) }
    } else {
        rememberInfiniteTransition(label = "skeleton").animateFloat(
            initialValue = 1f,
            targetValue = PULSE_LOW,
            animationSpec = infiniteRepeatable(tween(PULSE_MS, easing = LinearEasing), RepeatMode.Reverse),
            label = "skeletonPulse"
        )
    }
    CompositionLocalProvider(LocalSkeletonPulse provides pulse) {
        Column(
            modifier = modifier
                .testTag(SKELETON_TAG)
                .clearAndSetSemantics { contentDescription = description },
            content = content
        )
    }
}

/** A grey shape of the size [modifier] gives it (an icon, a chart, a figure). */
@Composable
fun SkeletonBlock(modifier: Modifier = Modifier, shape: Shape = RoundedCornerShape(8.dp), color: Color = Color.Unspecified) {
    val pulse = LocalSkeletonPulse.current
    val fill = if (color.isSpecified) color else LocalContentColor.current.copy(alpha = SKELETON_ALPHA)
    Box(
        modifier
            .graphicsLayer { alpha = pulse.value }
            .clip(shape)
            .background(fill)
    )
}

/**
 * [lines] lines of text in [style]: each takes the style's line height (so the text that
 * replaces it is as tall), the bar a little thinner than the letters; the last one shorter.
 */
@Composable
fun SkeletonText(
    modifier: Modifier = Modifier,
    lines: Int = 1,
    style: TextStyle = MaterialTheme.typography.bodyMedium,
    /** The width of the first lines, of the space (1: all of it). */
    widthFraction: Float = 1f,
    /** The last line's width, of the space (when there are several). */
    lastLineFraction: Float = 0.6f
) {
    val density = LocalDensity.current
    val lineHeight: Dp = with(density) {
        val height = if (style.lineHeight.isSpecified) style.lineHeight else style.fontSize * 1.4f
        if (height.isSpecified) height.toDp() else 20.dp
    }
    val barHeight: Dp = with(density) { if (style.fontSize.isSpecified) (style.fontSize * 0.8f).toDp() else lineHeight * 0.6f }
    Column(modifier) {
        repeat(lines.coerceAtLeast(1)) { index ->
            val fraction = if (lines > 1 && index == lines - 1) lastLineFraction else widthFraction
            Box(Modifier.fillMaxWidth().height(lineHeight), contentAlignment = Alignment.CenterStart) {
                SkeletonBlock(Modifier.fillMaxWidth(fraction.coerceIn(0.1f, 1f)).height(barHeight), RoundedCornerShape(barHeight / 2))
            }
        }
    }
}

/**
 * A list row: a round picture or icon (when [leading]), a title line and [lines] - 1 thinner
 * lines under it, like a person's or a roster's row.
 */
@Composable
fun SkeletonListRow(modifier: Modifier = Modifier, leading: Boolean = true, lines: Int = 2, leadingSize: Dp = 40.dp, leadingShape: Shape = CircleShape) {
    Row(modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
        if (leading) {
            SkeletonBlock(Modifier.size(leadingSize), leadingShape)
            Spacer(Modifier.width(16.dp))
        }
        Column(Modifier.weight(1f)) {
            SkeletonText(style = MaterialTheme.typography.titleMedium, widthFraction = 0.55f)
            if (lines > 1) SkeletonText(lines = lines - 1, style = MaterialTheme.typography.bodySmall, widthFraction = 0.8f, lastLineFraction = 0.4f)
        }
    }
}

/**
 * A card on its way: the [RegularCard] frame with its title (the real [title] when it is known
 * before the data, e.g. "Today at work", else a line), [lines] lines of text, then a
 * [blockHeight] block (a chart, a big figure) when given. Inside its own [SkeletonArea].
 */
@Composable
fun SkeletonCard(modifier: Modifier = Modifier, lines: Int = 2, blockHeight: Dp = 0.dp, title: String? = null, titleLine: Boolean = true) {
    SkeletonArea(modifier.fillMaxWidth()) {
        RegularCard(Modifier.fillMaxWidth()) {
            Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                if (title != null) {
                    Text(
                        title,
                        style = MaterialTheme.typography.titleMedium,
                        fontWeight = FontWeight.Bold,
                        color = LocalContentColor.current.copy(alpha = 0.7f),
                        maxLines = 1
                    )
                } else if (titleLine) {
                    SkeletonText(style = MaterialTheme.typography.titleMedium, widthFraction = 0.45f)
                }
                if (lines > 0) SkeletonText(lines = lines, widthFraction = 0.9f)
                if (blockHeight > 0.dp) SkeletonBlock(Modifier.fillMaxWidth().height(blockHeight))
            }
        }
    }
}

/** A list's row card on its way: the [RegularCard] frame around a [SkeletonListRow]. */
@Composable
fun SkeletonRowCard(modifier: Modifier = Modifier, leading: Boolean = true, lines: Int = 2, leadingShape: Shape = CircleShape) {
    RegularCard(modifier.fillMaxWidth()) {
        SkeletonListRow(Modifier.padding(16.dp), leading = leading, lines = lines, leadingShape = leadingShape)
    }
}

/** [count] row cards on their way, one [SkeletonArea] (a list whose first page is not known yet). */
@Composable
fun SkeletonList(modifier: Modifier = Modifier, count: Int = 4, leading: Boolean = true, lines: Int = 2, spacing: Dp = 12.dp, leadingShape: Shape = CircleShape) {
    SkeletonArea(modifier.fillMaxWidth()) {
        Column(verticalArrangement = Arrangement.spacedBy(spacing)) {
            repeat(count) { SkeletonRowCard(leading = leading, lines = lines, leadingShape = leadingShape) }
        }
    }
}

/**
 * A section that is not known yet ([known] false) shows [placeholder]; once known, [content]
 * fades in over it and the box resizes smoothly (no jump). Known from the start (cached data):
 * the content at once, no fade. [content] is a column ([spacing] between its parts).
 */
@Composable
fun LoadingSwap(
    known: Boolean,
    placeholder: @Composable () -> Unit,
    modifier: Modifier = Modifier,
    spacing: Dp = 16.dp,
    content: @Composable ColumnScope.() -> Unit
) {
    val reduced = LocalReducedMotion.current
    AnimatedContent(
        targetState = known,
        modifier = modifier,
        transitionSpec = { fadeThrough(reduced) },
        contentAlignment = Alignment.TopStart,
        label = "loadingSwap"
    ) { shown ->
        if (shown) {
            Column(verticalArrangement = Arrangement.spacedBy(spacing), content = content)
        } else {
            placeholder()
        }
    }
}
